import { useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";
import { useSetAtom } from "jotai";
import { Popover as PopoverPrimitive } from "radix-ui";
import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "~/components/ui/button";
import { Form, FormControl, FormField, FormItem } from "~/components/ui/form";
import { CheckIcon } from "~/components/ui/icons/check";
import { CloseIcon } from "~/components/ui/icons/close";
import { toast } from "~/components/ui/toast";
import {
  SetStockErrorCode,
  useSetStockMutation,
} from "~/graphql/graphql-new.gen";
import { isScopeAuthorized } from "~/libs/auth";

import { activeStockIdState } from "./stock-state";

type UpdateStockModalProps = {
  previousStock: number | null | undefined;
  stockId: string;
};

type StockUpdateForm = {
  stock: number;
};

export const UpdateStockModal: React.FC<UpdateStockModalProps> = ({
  previousStock,
  stockId,
}) => {
  const { user } = useAuth0();
  const { t } = useTranslation();

  const setActiveStockId = useSetAtom(activeStockIdState);

  const { mutateAsync: setStockMutation, isPending } = useSetStockMutation();

  const form = useForm<StockUpdateForm>({
    mode: "onTouched",
    disabled: !isScopeAuthorized(user, "scope.brand.stock.write"),
    defaultValues: {
      stock: previousStock ?? 0,
    },
  });

  const onSubmit = async (data: StockUpdateForm) => {
    try {
      const result = await setStockMutation({
        input: { stockId: stockId, quantity: data.stock },
      });
      if (result.setStock === SetStockErrorCode.UpdateDisabled) {
        toast.error(t("stock.stock_shopify_error"));
      } else if (result.setStock === SetStockErrorCode.QuantityNotValid) {
        toast.error(t("stock.stock_quantity_error"));
      } else if (result.setStock === SetStockErrorCode.StockNotFound) {
        toast.error(t("stock.stock_generic_error"));
      } else {
        toast.success(t("stock.updated_stock"));
      }
    } catch (error) {
      toast.error(t("stock.stock_generic_error"));
      Sentry.captureException(error);
    }
    setActiveStockId(undefined);
  };

  return (
    <Form {...form}>
      <form className="space-y-2" onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          name="stock"
          rules={{
            required: true,
            min: 0,
            validate: {
              different: (value: number) => value !== previousStock,
            },
          }}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <input
                  type="number"
                  autoFocus={true}
                  {...field}
                  onChange={(event) => {
                    field.onChange(event.target.valueAsNumber);
                  }}
                  className={
                    'hide-arrows form-input block w-full rounded-lg border border-gray-500 p-2 text-right text-sm leading-5.5 text-gray-900 outline-none transition duration-300 placeholder:text-gray-500 hover:border-gray-700 focus:border-gray-700 focus:ring-gray-700 disabled:cursor-not-allowed disabled:border-gray-100 disabled:bg-gray-50 disabled:text-gray-500 disabled:placeholder:text-gray-300 aria-[invalid="true"]:border-red-600 aria-[invalid="true"]:ring-red-600'
                  }
                />
              </FormControl>
            </FormItem>
          )}
        />
        <div className="flex w-full flex-row justify-between">
          <PopoverPrimitive.Close asChild>
            <Button
              type="button"
              variant="outline"
              className="h-8 w-[36px] justify-center p-0"
            >
              <CloseIcon className="size-6" />
            </Button>
          </PopoverPrimitive.Close>
          <Button
            type="submit"
            className="h-8 w-[36px] justify-center p-0"
            disabled={
              !form.formState.isValid || form.formState.disabled || isPending
            }
          >
            <CheckIcon className="size-6" />
          </Button>
        </div>
      </form>
    </Form>
  );
};
